import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import {
    placeOrder,
    PlaceOrderRequest,
    PlaceOrderResponse,
    PlaceOrderResult,
    PaymentProviderResponse,
    ShippingOption,
} from 'common/api/e-comm/orders/service';
import { CheckoutRequest } from 'common/types/CheckoutRequest';
import { ThreeDSResponse, ThreeDSTransactionError } from 'common/modules/3dsecure';
import { hasServerError } from 'common/api/utils/hasServerError';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { OrderSource } from 'common/api/e-comm/models/OrderSource';
import { APIResponse, UnsuccessfulResponseWithBody } from 'common/api/models';
import { emptyHomeDetailsResponse } from 'common/api/crm/home/models/HomeDetailsResponse';
import { voidPayment } from 'common/api/e-comm/credit-cards/service';
import { PaymentType } from 'common/api/e-comm/payment-types';
import { isDefined } from 'common/utils';
import { setDefaultPaymentMethodThunk } from 'common/features/store/duck/account/duck';
import { useBlackBoxValues } from 'modules/fraud-force/fraudForce';
import { gaLogPurchase } from 'config/analytics';
import { isInIframe, mainWebsiteOrigin } from 'utils';
import { useBlackBoxRequestFromParentWindow } from 'hooks/useBlackBoxRequestFromParentWindow';

const isShipToHomeEnabled = () => true;

const hasResult =
    (result: PlaceOrderResult) =>
    (response: APIResponse<PlaceOrderResponse, PlaceOrderResponse>) => {
        if (response.data) {
            return response.data.result === result;
        }

        return Boolean(response.error.body && response.error.body.result === result);
    };

const isOrderOnHold = hasResult(PlaceOrderResult.Hold);
const exceedsFinCenLimit = hasResult(PlaceOrderResult.ExceedsFinCenLimit);
const hasUnavailableItems = hasResult(PlaceOrderResult.ContainsUnavailableItems);
const creditCardRejected = hasResult(PlaceOrderResult.CreditCardPaymentRejected);

interface PlaceOrderResponseWithActualEarnings extends PlaceOrderResponse {
    actualEarnings: number;
}

const hasEarningsMismatchError = (
    response: APIResponse<PlaceOrderResponse, PlaceOrderResponse>
): response is UnsuccessfulResponseWithBody<PlaceOrderResponseWithActualEarnings> => {
    return hasResult(PlaceOrderResult.EarningsMismatch)(response);
};

const checkoutServerErrorConfig = {
    title: 'Transaction Could Not Be Completed',
    message: 'There was an error from the card issuer. Please try again later.',
};

const checkoutBlackboxErrorConfig = {
    title: 'Transaction Could Not Be Completed',
    message: 'There was an error processing your transaction. Please try again later.',
};

export interface useSubmitOrderProps {
    openLoader: () => void;
    closeLoader: () => void;
    clientVersion: string;
    report: (msg: { title?: string; message?: string }) => void;
    backToCart: () => void;
    onHold: () => void;
    onSuccess: () => void;
    onCreditCardError: () => void;
    doFinCENAlert: () => void;
    doEarningsMismatchAlert: (earnings: number) => Promise<boolean>;
    onUnavailableItems: () => void;
    shouldUpdateDefaultPayment: boolean;
    shippingMethod?: ShippingOption;
    cartTotal: number;
}

export const useSubmitOrder = (params: useSubmitOrderProps) => {
    const {
        openLoader,
        closeLoader,
        clientVersion = '',
        report,
        backToCart,
        onCreditCardError,
        doFinCENAlert,
        doEarningsMismatchAlert,
        onUnavailableItems,
        shouldUpdateDefaultPayment,
        shippingMethod,
        cartTotal,
    } = params;

    const dispatch = useDispatch<any>();

    const doSetPaymentMethodAsDefault = useCallback(
        (values: CheckoutRequest, creditCardId: number | null) => {
            const { paymentMethod } = values;

            if (!isDefined(paymentMethod)) {
                return;
            }

            if (paymentMethod.paymentType === PaymentType.CreditCard) {
                if (!values.useNewCreditCard && !values.useNewDebitCard) {
                    return dispatch(setDefaultPaymentMethodThunk(paymentMethod));
                }

                if (!values.newCreditCard.save || !isDefined(creditCardId)) {
                    return;
                }

                return dispatch(
                    setDefaultPaymentMethodThunk({
                        ...paymentMethod,
                        creditCardId,
                    })
                );
            }

            return dispatch(setDefaultPaymentMethodThunk(paymentMethod));
        },
        [dispatch]
    );

    const homeDetails = useHomeDetails() || emptyHomeDetailsResponse;
    const { retrieveBlackBox } = useBlackBoxValues(!isInIframe());
    const { sendBlackBoxRequestToParentWindow } = useBlackBoxRequestFromParentWindow();

    // eslint-disable-next-line complexity
    const submitOrder = useCallback(
        async (
            _cart: CartResponse,
            values: CheckoutRequest,
            transactionId: string | null,
            paymentProviderResponse: PaymentProviderResponse | null,
            payment3DSecureResponse: ThreeDSResponse | ThreeDSTransactionError | null
        ): Promise<APIResponse<PlaceOrderResponse, PlaceOrderResponse> | void> => {
            if (!values.paymentMethod) {
                return;
            }

            // const total = getCartTotal({ paymentMethod: values.paymentMethod, shippingMethod });
            const bbValues = isInIframe()
                ? await sendBlackBoxRequestToParentWindow()
                : await retrieveBlackBox();
            if (bbValues.blackBoxValue === null) {
                report(checkoutBlackboxErrorConfig);
                return;
            }
            const { firstName, lastName, email } = homeDetails.profile;
            const placeOrderApiVersion = isShipToHomeEnabled() ? '2' : '1.1';
            const { savePaymentDetails } = values;

            // ^ https://glscrip.atlassian.net/browse/S30-2559?focusedCommentId=49994&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-49994
            // placeOrder v2 assumes that the order's billing method and shipping information have both
            // been stored using the /orders/billing and /orders/shipping APIs (read: it is NOT backwards compatible)
            const requestBody: PlaceOrderRequest = {
                blackBoxValue: bbValues.blackBoxValue || '',
                secondaryBlackBoxValue: bbValues.secondaryBlackBoxValue || '',
                paymentProviderResponse,
                payment3DSecureResponse,
                cartId: _cart.id,
                expectedEarnings: _cart.earnings,
                paymentMethod: values.paymentMethod,
                orderSource: OrderSource.Web,
                clientVersion,
                firstName,
                lastName,
                email,
                savePaymentDetails,
            };

            const response = await placeOrder(requestBody, placeOrderApiVersion);
            await closeLoader();

            if (hasUnavailableItems(response)) {
                await report({
                    title: 'Some items in your cart are unavailable.',
                });

                onUnavailableItems();
                return;
            }

            const isOnHold = isOrderOnHold(response);
            const hasEarningsMismatch = hasEarningsMismatchError(response);

            if (!isOnHold && !hasEarningsMismatch && response.error) {
                if (creditCardRejected(response)) {
                    return onCreditCardError();
                } else if (exceedsFinCenLimit(response)) {
                    return doFinCENAlert();
                } else if (values.paymentMethod.paymentType === PaymentType.CreditCard) {
                    if (transactionId) {
                        await voidPayment(transactionId);
                    }

                    if (hasServerError(response)) {
                        return report(checkoutServerErrorConfig);
                    }
                }

                return;
            }

            if (hasEarningsMismatchError(response)) {
                const actualEarnings = response.error.body.actualEarnings;

                const shouldContinue = await doEarningsMismatchAlert(actualEarnings);

                if (!shouldContinue) {
                    if (transactionId) {
                        await openLoader();
                        await voidPayment(transactionId);
                        await closeLoader();
                    }

                    return backToCart();
                }

                await openLoader();

                return submitOrder(
                    { ..._cart, earnings: actualEarnings },
                    values,
                    transactionId,
                    paymentProviderResponse,
                    payment3DSecureResponse
                );
            }

            if (shouldUpdateDefaultPayment) {
                doSetPaymentMethodAsDefault(
                    values,
                    response.data ? response.data.newCreditCardId : null
                );
            }

            if (response.data) {
                const gaData = {
                    cart: _cart,
                    total: cartTotal,
                    shippingCost: shippingMethod?.cost,
                    orderId: response.data.orderId ? String(response.data.orderId) : '',
                    isBuyNow: false,
                };
                if (isInIframe()) {
                    window.parent.postMessage({ type: 'logPurchase', gaData }, mainWebsiteOrigin());
                } else {
                    gaLogPurchase(gaData);
                }
            }

            return response;
        },
        [
            homeDetails.profile,
            shouldUpdateDefaultPayment,
            onUnavailableItems,
            onCreditCardError,
            backToCart,
            doSetPaymentMethodAsDefault,
            clientVersion,
            closeLoader,
            doEarningsMismatchAlert,
            doFinCENAlert,
            openLoader,
            report,
            retrieveBlackBox,
            shippingMethod,
            cartTotal,
            sendBlackBoxRequestToParentWindow,
        ]
    );

    return { submitOrder };
};
